import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { http } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, Panel, Confirm, SortableList, SingleImage } from "./ui";

const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function CategoryCard({ cat, onSaved, onDelete }) {
  const [c, setC] = useState(cat);
  const [dirty, setDirty] = useState(false);
  const set = (k, v) => { setC((x) => ({ ...x, [k]: v })); setDirty(true); };
  const setSub = (subs) => set("subcategories", subs);
  async function save() {
    const subs = (c.subcategories || []).filter((s) => s.name).map((s) => ({ name: s.name, slug: s.slug || slugify(s.name) }));
    await http.put(`/categories/${c.id}`, { ...c, subcategories: subs });
    toast.success("Categoría guardada");
    setDirty(false);
    onSaved();
  }
  return (
    <Panel className="!p-4" >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid={`category-card-${c.slug}`}>
        <div className="space-y-3">
          <div><Label>Nombre</Label><Input value={c.name} onChange={(e) => set("name", e.target.value)} data-testid={`cat-name-${c.slug}`} /></div>
          <div className="text-xs text-neutral-500">Enlace: /catalogo/{c.slug}</div>
          <div><Label>Imagen del recuadro (inicio)</Label><SingleImage value={c.image} onChange={(v) => set("image", v)} testid={`cat-image-${c.slug}`} /></div>
        </div>
        <div className="md:col-span-2">
          <Label>Subcategorías (arrastra para ordenar)</Label>
          <div className="mt-2">
            <SortableList items={(c.subcategories || []).map((s, i) => ({ ...s, _k: s.slug || `n${i}` }))} getId={(s) => s._k}
              onReorder={(l) => setSub(l.map(({ _k, ...s }) => s))}
              renderItem={(s, i) => (
                <div className="flex gap-2">
                  <Input value={s.name} onChange={(e) => setSub(c.subcategories.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} data-testid={`sub-name-${c.slug}-${i}`} />
                  <Button type="button" size="icon" variant="outline" onClick={() => setSub(c.subcategories.filter((_, j) => j !== i))}><Trash2 size={14} /></Button>
                </div>
              )} />
            <Button size="sm" variant="outline" className="mt-2" onClick={() => setSub([...(c.subcategories || []), { name: "", slug: "" }])} data-testid={`add-sub-${c.slug}`}><Plus size={14} className="mr-1" /> Subcategoría</Button>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Confirm onConfirm={() => onDelete(c)} title={`¿Eliminar la categoría ${c.name}?`} description="Los productos de esta categoría no se borran, pero quedarán sin categoría visible."
          trigger={<Button variant="outline" size="sm" className="text-red-600" data-testid={`delete-cat-${c.slug}`}><Trash2 size={14} className="mr-1" /> Eliminar</Button>} />
        <Button size="sm" className="btn-primary" disabled={!dirty} onClick={save} data-testid={`save-cat-${c.slug}`}><Save size={14} className="mr-1" /> Guardar</Button>
      </div>
    </Panel>
  );
}

export default function CategoriesPage() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const load = () => http.get("/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);

  async function create() {
    if (!name.trim()) return;
    await http.post("/categories", { name, slug: slugify(name), order: cats.length + 1, subcategories: [] });
    setName("");
    toast.success("Categoría creada");
    load();
  }
  async function reorder(list) {
    setCats(list);
    await Promise.all(list.map((c, i) => http.put(`/categories/${c.id}`, { ...c, order: i + 1 })));
    toast.success("Orden guardado");
  }
  async function remove(c) {
    try {
      await http.delete(`/categories/${c.id}`);
      toast.success("Categoría eliminada");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar");
    }
  }

  return (
    <div data-testid="admin-categories-page">
      <PageHeader title="Categorías" subtitle="Edita nombres, subcategorías, fotos y el orden en que aparecen" />
      <Panel className="mb-6">
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre de nueva categoría" data-testid="new-category-name" />
          <Button className="btn-primary" onClick={create} data-testid="new-category-btn"><Plus size={14} className="mr-1" /> Crear</Button>
        </div>
      </Panel>
      <SortableList items={cats} onReorder={reorder} className="space-y-4"
        renderItem={(c) => <CategoryCard key={c.id + (c.updated_at || "")} cat={c} onSaved={load} onDelete={remove} />} />
    </div>
  );
}
